import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfig } from '../context/ConfigContext';
import {
  ADMIN_COLORS,
  ERROR_LOG_SOURCES,
  ERROR_LOG_SOURCE_LABELS,
  DEFAULT_ERROR_LOG_DAYS,
  ERROR_LOG_RETENTION_DAYS,
} from '../constants/bookingConst';
import { spinnerRing } from '../utils/bookingUtils';
import {
  fetchErrorLogInRange,
  groupErrorsByDay,
  downloadErrorLogTxt,
  deleteErrorLogEntries,
  pruneErrorLogsOlderThan,
  getErrorLogSessionStats,
  writeErrorLogTestEntry,
} from '../utils/errorLogUtil';
import Modal from './Modal';
import ErrorLogFileRow from './ErrorLogFileRow';
import ErrorLogDetailModal from './ErrorLogDetailModal';
import AdminSlotLockPanel from './AdminSlotLockPanel';
import '../css/AdminErrorLogs.css';

const COLORS = ADMIN_COLORS;

/** "YYYY-MM-DD" for `n` days ago, local time. */
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const LEVEL_OPTIONS = [
  { value: 'all', label: 'All levels' },
  { value: 'error', label: 'Errors only' },
  { value: 'warn', label: 'Warnings only' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  ...Object.values(ERROR_LOG_SOURCES).map(value => ({
    value,
    label: ERROR_LOG_SOURCE_LABELS[value] || value,
  })),
];

/**
 * Admin > Error Logs.
 *
 * Reads back what the browser console recorded on a live deployment, grouped
 * into one downloadable .txt per day, newest day first. Nothing text-shaped
 * is stored — the file is rendered from the records when Download is pressed,
 * and it honours the filters currently applied, so "just the PERMISSION_DENIED
 * ones from Tuesday" is a real file rather than a file you then grep.
 *
 * A ONE-SHOT read, not a live subscription: this node is written to by every
 * visitor, and a listener left open on a bad day would re-download the range
 * on every single write. Refresh is explicit.
 *
 * @returns {JSX.Element}
 */
function AdminErrorLogs() {
  const { config } = useConfig();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed read and an empty log must never look the same here — an admin
  // opening this screen is already suspicious that something is broken.
  const [loadError, setLoadError] = useState(null);
  const [busyMessage, setBusyMessage] = useState('');

  const [dateFilter, setDateFilter] = useState({
    from: isoDaysAgo(DEFAULT_ERROR_LOG_DAYS - 1),
    to: isoDaysAgo(0),
  });
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDay, setExpandedDay] = useState(null);

  const [selectedEntry, setSelectedEntry] = useState(null);
  // { title, message, confirmText, onConfirm } — one confirm dialog reused for
  // every destructive action on this screen.
  const [confirm, setConfirm] = useState(null);
  const [notice, setNotice] = useState(null);

  const clubName = config?.courtName || 'Court Booking System';
  const isRanged = Boolean(dateFilter.from && dateFilter.to);

  const load = useCallback(async () => {
    if (!isRanged) { setEntries([]); setLoading(false); return; }
    setLoading(true);
    try {
      const start = new Date(`${dateFilter.from}T00:00:00`).getTime();
      const end = new Date(`${dateFilter.to}T23:59:59.999`).getTime();
      const list = await fetchErrorLogInRange(start, end);
      setEntries(list);
      setLoadError(null);
    } catch (e) {
      setLoadError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [isRanged, dateFilter.from, dateFilter.to]);

  useEffect(() => { load(); }, [load]);

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (levelFilter !== 'all') list = list.filter(e => (e.level || 'error') === levelFilter);
    if (sourceFilter !== 'all') list = list.filter(e => e.source === sourceFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(e => (
        (e.message || '').toLowerCase().includes(q)
        || (e.code || '').toLowerCase().includes(q)
        || (e.screen || '').toLowerCase().includes(q)
        || (e.actor || '').toLowerCase().includes(q)
        || (e.url || '').toLowerCase().includes(q)
        || (e.sessionId || '').toLowerCase().includes(q)
      ));
    }
    return list;
  }, [entries, levelFilter, sourceFilter, searchQuery]);

  const dayGroups = useMemo(() => groupErrorsByDay(filteredEntries), [filteredEntries]);

  const stats = useMemo(() => {
    const errorCount = filteredEntries.filter(e => (e.level || 'error') !== 'warn').length;
    const codes = new Map();
    filteredEntries.forEach((e) => {
      if (!e.code) return;
      codes.set(e.code, (codes.get(e.code) || 0) + 1);
    });
    const topCodes = Array.from(codes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([code, count]) => ({ code, count }));

    return {
      total: filteredEntries.length,
      errorCount,
      warnCount: filteredEntries.length - errorCount,
      dayCount: dayGroups.length,
      // How many distinct browser tabs hit these errors. One session with 40
      // entries is a single unlucky visitor; 40 sessions with one each is
      // everybody.
      sessionCount: new Set(filteredEntries.map(e => e.sessionId).filter(Boolean)).size,
      topCodes,
    };
  }, [filteredEntries, dayGroups]);

  /** Renders and saves one day's file, honouring the current filters. */
  const handleDownloadDay = (group) => {
    downloadErrorLogTxt(group.entries, { clubName, label: group.dayKey });
  };

  /** Everything currently on screen as a single file across the range. */
  const handleDownloadAll = () => {
    downloadErrorLogTxt(filteredEntries, {
      clubName,
      label: `${dateFilter.from}_to_${dateFilter.to}`,
    });
  };

  const runBusy = async (message, fn, doneMessage) => {
    setBusyMessage(message);
    try {
      const result = await fn();
      await load();
      setNotice({ type: 'success', title: 'Done', message: doneMessage(result) });
    } catch (e) {
      setNotice({ type: 'error', title: 'That did not work', message: e?.message || String(e) });
    } finally {
      setBusyMessage('');
    }
  };

  const handleDeleteDay = (group) => {
    setConfirm({
      title: `Delete errors-${group.dayKey}.txt?`,
      message: `This permanently removes ${group.entries.length} entr${group.entries.length === 1 ? 'y' : 'ies'} from ${group.dayKey}. Download the file first if you still need it — deleted entries cannot be recovered.`,
      confirmText: 'Delete',
      onConfirm: () => {
        setConfirm(null);
        runBusy(
          'Deleting entries',
          () => deleteErrorLogEntries(group.entries),
          (n) => `${n} entr${n === 1 ? 'y' : 'ies'} deleted.`,
        );
      },
    });
  };

  const handlePrune = () => {
    setConfirm({
      title: `Clear entries older than ${ERROR_LOG_RETENTION_DAYS} days?`,
      message: `Anything logged more than ${ERROR_LOG_RETENTION_DAYS} days ago is removed. This keeps the log a triage tool rather than a growing store — nothing else in the app reads it.`,
      confirmText: 'Clear old entries',
      onConfirm: () => {
        setConfirm(null);
        runBusy(
          'Clearing old entries',
          () => pruneErrorLogsOlderThan(ERROR_LOG_RETENTION_DAYS),
          (n) => (n === 0 ? 'Nothing was old enough to clear.' : `${n} old entr${n === 1 ? 'y' : 'ies'} cleared.`),
        );
      },
    });
  };

  /**
   * Proves the pipeline end to end on a deployment where nothing has failed
   * yet — without it, an empty screen is ambiguous between "no errors" and
   * "logging never reached the database".
   */
  const handleTestEntry = () => {
    runBusy(
      'Writing a test entry',
      () => writeErrorLogTestEntry(),
      (result) => (result.sent
        ? 'Test entry written. If it does not appear below, the write reached the database but the read did not — check the /errorLogs rule.'
        : `The entry was captured but not accepted by the database — ${result.buffered} entr${result.buffered === 1 ? 'y is' : 'ies are'} now held on this device and will retry later. That usually means the /errorLogs rule has not been deployed, or this browser is not signed in.`),
    );
  };

  const sessionStats = getErrorLogSessionStats();

  return (
    <div className="admin-error-logs">
      <div className="ael-header">
        <h2 className="section-title">
          Error Logs
          <span className="ael-count-badge">{stats.total}</span>
        </h2>
        <div className="ael-header-actions">
          <button type="button" className="ael-btn ael-btn-ghost" onClick={load} disabled={loading}>
            Refresh
          </button>
          <button type="button" className="ael-btn ael-btn-ghost" onClick={handleTestEntry}>
            Write test entry
          </button>
          <button
            type="button"
            className="ael-btn ael-btn-primary"
            onClick={handleDownloadAll}
            disabled={filteredEntries.length === 0}
          >
            Download all shown
          </button>
        </div>
      </div>

      {/* ---- Summary ---- */}
      <div className="ael-stat-grid">
        <div className="ael-card">
          <div className="ael-card-title">In this range</div>
          <div className="ael-stat-row">
            <div className="ael-stat">
              <span className="ael-stat-value" style={{ color: COLORS.danger }}>{stats.errorCount}</span>
              <span className="ael-stat-label">Errors</span>
            </div>
            <div className="ael-stat">
              <span className="ael-stat-value" style={{ color: COLORS.warning }}>{stats.warnCount}</span>
              <span className="ael-stat-label">Warnings</span>
            </div>
            <div className="ael-stat">
              <span className="ael-stat-value">{stats.dayCount}</span>
              <span className="ael-stat-label">Files</span>
            </div>
            <div className="ael-stat">
              <span className="ael-stat-value">{stats.sessionCount}</span>
              <span className="ael-stat-label">Browser sessions</span>
            </div>
          </div>
        </div>

        <div className="ael-card">
          <div className="ael-card-title">Most frequent codes</div>
          {stats.topCodes.length === 0 ? (
            <p className="ael-empty-note">No coded failures in this range.</p>
          ) : (
            <div className="ael-code-list">
              {stats.topCodes.map(row => (
                <button
                  key={row.code}
                  type="button"
                  className="ael-code-chip"
                  onClick={() => setSearchQuery(row.code)}
                  title={`Filter to ${row.code}`}
                >
                  <span className="ael-code-chip-name">{row.code}</span>
                  <span className="ael-code-chip-count">{row.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Filters ---- */}
      <div className="ael-filters">
        <div className="ael-search">
          <input
            type="text"
            placeholder="Search message, code, screen, page, admin or session id..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select className="ael-select" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
          {LEVEL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <select className="ael-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
          {SOURCE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <div className="ael-date-filter">
          <label htmlFor="ael-from">From</label>
          <input
            id="ael-from"
            type="date"
            value={dateFilter.from}
            onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
          />
          <label htmlFor="ael-to">To</label>
          <input
            id="ael-to"
            type="date"
            value={dateFilter.to}
            min={dateFilter.from || undefined}
            onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
          />
        </div>
      </div>

      <p className="ael-note">
        One file per day, newest first. The .txt is built when you press Download and includes exactly
        what the filters above are showing — nothing is stored as text.
        {' '}This tab has logged {sessionStats.written} of a possible {sessionStats.cap} entries.
        {/* An error captured before sign-in cannot be written — the rules
            need an identity — so it waits on the device instead of being
            dropped. A non-zero count here on an otherwise healthy screen is
            the tell that writes are being refused. */}
        {sessionStats.buffered > 0 && (
          <>
            {' '}<strong>{sessionStats.buffered}</strong> entr{sessionStats.buffered === 1 ? 'y is' : 'ies are'} held
            on this device waiting to upload.
          </>
        )}
        {sessionStats.suspended ? ' Logging is suspended on this tab after repeated failed writes; the held entries retry on the next page load.' : ''}
      </p>

      {/* ---- Files ---- */}
      {busyMessage ? (
        <div className="ael-busy">{spinnerRing(busyMessage, config?.logoUrl)}</div>
      ) : loadError ? (
        <div className="ael-error">
          <strong>Could not read the error log.</strong>
          <p>{loadError}</p>
          <p className="ael-error-hint">
            A “permission denied” here means <code>/errorLogs</code> has no rule in
            database.rules.json yet; “Index not defined” means it needs
            <code> ".indexOn": ["timestamp"]</code>. Errors are still being
            captured either way — this screen just can’t read them back.
          </p>
        </div>
      ) : loading ? (
        <div className="ael-busy">{spinnerRing('Loading error logs', config?.logoUrl)}</div>
      ) : !isRanged ? (
        <p className="placeholder-text">Pick a date range to load error logs.</p>
      ) : dayGroups.length === 0 ? (
        <p className="placeholder-text">
          No errors logged for these filters. That is the good outcome — use “Write test entry” if you
          want to confirm logging is reaching the database.
        </p>
      ) : (
        <div className="ael-file-list">
          {dayGroups.map(group => (
            <ErrorLogFileRow
              key={group.dayKey}
              group={group}
              expanded={expandedDay === group.dayKey}
              onToggle={(dayKey) => setExpandedDay(prev => (prev === dayKey ? null : dayKey))}
              onDownload={handleDownloadDay}
              onDelete={handleDeleteDay}
              onSelectEntry={setSelectedEntry}
            />
          ))}
        </div>
      )}

      <div className="ael-footer-actions">
        <button type="button" className="ael-btn ael-btn-danger-ghost" onClick={handlePrune}>
          Clear entries older than {ERROR_LOG_RETENTION_DAYS} days
        </button>
      </div>

      {/* Slot locks live here rather than on a screen of their own: this one
          is already config-admin only (AdminSidebar's configAdminOnly +
          AdminPage's isConfigAdmin gate), and a stuck lock is exactly the
          kind of "why is the site refusing bookings" question someone opens
          Error Logs to answer. It holds its own state and reads the database
          itself, so it costs this screen nothing until it renders. */}
      <AdminSlotLockPanel />

      <ErrorLogDetailModal
        isOpen={Boolean(selectedEntry)}
        entry={selectedEntry}
        clubName={clubName}
        onClose={() => setSelectedEntry(null)}
      />

      <Modal
        isOpen={Boolean(confirm)}
        type="warning"
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'Confirm'}
        showCancel
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />

      <Modal
        isOpen={Boolean(notice)}
        type={notice?.type || 'info'}
        title={notice?.title || ''}
        message={notice?.message || ''}
        onClose={() => setNotice(null)}
      />
    </div>
  );
}

export default AdminErrorLogs;
